import Container from "@/components/commons/Container";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { Badge } from "@/v2-components/ui/badge";

import {
  COOLING_FAN_FIGURES,
  COOLING_FAN_SPEC_VALUES,
  type CoolingFanFigureSlot,
} from "./cooling-fan-assets";
import { CoolingFanFigure } from "./cooling-fan-figure";
import { CheckMarkIcon, CrossMarkIcon } from "./cooling-fan-icons";

/**
 * 쿨링 팬 판매 페이지의 **제품별 섹션** (BHTP01A1D).
 *
 * ## 이 파일이 담는 것 / 담지 않는 것
 *
 * PDP 는 `ProductFeatureSections` 의 `switch (product.slug)` 로 갈린다. 공용 블록은
 * 그쪽이 그리므로 여기엔 **시안에만 있는 세 섹션**만 둔다.
 *
 * ```
 * 여기            제품 소개 · 사용 방법(+충전 안내) · 제품 사양
 * 공용            갤러리 · 구매 혜택 3카드 · 소프트웨어 3카드 · FAQ · 뉴스레터 · 푸터
 * mgmt 데이터     갤러리 이미지(productImages) · 가격 · 재고
 * ```
 *
 * ## ★시안이 두 벌이고 **서로 다르다** — 브레이크포인트로 각각 따른다
 *
 * 2026-09-29 「완성본」 전수 재조회 · `8X5fPtMR3n9lVij5MJKaUh`
 * · Desktop `30491:52700` (1920×6556) · Mobile `30810:38089` (360×7178).
 * 작성자 지시(2026-09-16): *"각각 다르게 구현"*.
 *
 * ```
 *                          모바일 (360)                    데스크탑 (1440)
 * 섹션 좌우                 16 (Container mx-4)             Container mx-5 / max-w 1440
 * 섹션 간 갭                64 (Gap mt-16)                  96 (Gap laptop:mt-24)
 * 섹션 제목·부제            24/26 · 14/22 .5                32/40 text-5xl · 16/22 .5 text-lg
 *
 * 제품 소개  배치           1단 2카드 · 간격 32              2단 2카드 · 간격 24
 *           커버            328×232 ×2                      708×500 ×2
 *           커버→텍스트      16                              20
 *           제목            18/26 Medium                    22/28 text-2xl Regular
 *           본문            14/22 (자간 0)                   16/22 .5 text-lg
 *
 * 사용 방법  스텝 배치        1단 · 간격 32                    3단 grid · 간격 24
 *           커버            328×213 r16                     464×302 r16
 *           배지·제목        **세로**(배지 위) · 18/26        가로 · 22/28 text-2xl
 *           본문·주석        14/22 .25 · 12/18 .4             14/18 text-base · 12/16 text-sm
 *
 * 충전 안내  블록           **풀블리드** px20 py24           r16 p40
 *           제목·부제        20/26 · 14/22 .25                24/32 text-3xl · 14/18 text-base
 *           카드 배치        이미지 320×192 위 / 텍스트 아래   사진 668×400 전면 + 텍스트 오버레이
 *           카드 제목        16/26 .5                         22/28 text-2xl
 *           카드 항목        14/18 .25 · 간격 4               16/22 .5 text-lg · 간격 4
 *
 * 제품 사양  내부 순서       도면(Front·Back 2장) → 패널     **타일 4 → 도면(1장)**
 *           값 배치          **2×2 한 패널 · 왼쪽 정렬**      4단 타일 · 가운데 정렬
 *           라벨·값          13/20 · 16/24                    16/22 .5 text-lg · 22/28 text-2xl
 * ```
 *
 * 섹션 순서는 2026-09-29 에 **두 보드가 같아졌다**(소프트웨어 → 사양) —
 * `ProductFeatureSections.tsx` 에서 폭별 `order` 를 걷었다.
 *
 * ## 2026-09-23 대비 바뀐 것
 *
 * - 3단 풍속이 **「Midnight Flow」** 합성 컷으로 교체(두 폭 모두 · `cooling-fan-assets.ts`).
 * - 「쉬운 탈부착」이 **모바일에서도** 빠졌다 — 제품 소개는 두 폭 모두 2카드.
 * - 충전 안내 소제목이 STEP 01 본문 복붙 → **확정 문장**(`30765:45295` · `30812:234987`).
 *   모바일 충전 카드 2장의 복붙(둘 다 「쿨링 팬 사용 시」)도 고쳐져 데스크탑과 내용이 같다.
 * - 사양 4번 칸 라벨 「TactSuit Pro 전용」 → **「호환 기기」**, 값이 「TactSuit Pro 전용」.
 *   2번 칸 라벨이 두 보드 모두 「냉각 방식」으로 통일(모바일 「송풍 팬」 소멸).
 * - 소개 본문이 두 보드 모두 **문장 경계에서 줄바꿈**한다(`31154:31622` · `30854:38635`).
 *   번역 값에 `\n` 을 심고 `whitespace-pre-line` 으로 그린다. 3단 풍속 본문은 **데스크탑만**
 *   줄을 바꾼다(모바일 `30854:38640` 은 한 문단) → `laptop:whitespace-pre-line`.
 *
 * ## 임의값 — 스케일에 없는 것만, 전부 시안 노드 근거
 *
 * 규칙(`a15889953`): ①리포 스케일(`tailwind.config.ts`)에 있으면 토큰 ②없으면 값 + **노드
 * 인용**. 스케일에 있던 `50px`·`60px` 은 `gap-12.5`·`gap-15` 로 바꿨다(리뷰 R1·R2 5번).
 * 2026-09-29 주석 제거 후 기계 추출 **28종** — 네 갈래다:
 *
 * ```
 * 폰트 크기/행간 (모바일)                         시안 노드 (Mobile)
 *   24/26  섹션 제목                             30812:234945 · 30812:235005
 *   14/22  섹션 부제 · 소개/스텝 본문 · 충전 부제   30812:234946 · 30854:38635 · 30812:234955
 *   18/26  소개·스텝 제목                         30854:38634 · 30812:234954
 *   20/26  충전 안내 제목                         30812:234986
 *   16/26  충전 카드 제목(행간만)                  30841:48112
 *   13/20 · 16/24  사양 라벨 · 값                 30812:235012 · 30812:235013
 *   12/18  STEP 03 주석(행간만)                   30812:234977
 * 자간  .5 · .25 · .4   리포 폰트 토큰에 자간이 없다 — 시안 텍스트 스타일 그대로
 * 종횡비  328/232 · 708/500 · 328/213 · 464/302 · 320/192 · 887/700 · 328.326/308.712
 *        = 각 커버 노드 크기(에셋 표는 `cooling-fan-assets.ts`)
 * 레이아웃 (데스크탑)                             시안 노드 (Desktop)
 *   gap 52       스텝 ↔ 충전 블록                 30765:45403
 *   h 400        충전 카드                        31045:38326
 *   max-w 800    사양 타일 줄                     30995:38410
 *   max-w 887    사양 도면                        30995:38711
 *   min-h 117.333  사양 타일(최소 높이 — SpecCell 주석)   30765:45376
 *   max-w calc(50%−25px)  충전 카드 텍스트 — ChargingCard 주석(모듈 경계 계산)
 * ```
 *
 * ⚠️ 색은 전부 토큰에 떨어진다 — `#21272A` coolgray-90 · `#697077` coolgray-60 ·
 * `#EDF5FF` blue-10 · `#0053FF` blue-hover-70. 시안 `Cool Gray/10 = #F2F4F7` vs 리포
 * `#F2F4F8` 만 파랑 1/255 어긋나는데 **토큰을 쓴다**.
 *
 * 제품 사양 크기·냉각 방식·무게 값은 시안이 아직 자리표시다 → `—` 로 그린다
 * (`COOLING_FAN_SPEC_VALUES`).
 */

/** 시안의 이미지 슬롯 하나. 전부 같은 모양이라 타입으로 묶는다. */
type FigureSlot = CoolingFanFigureSlot;

/**
 * 섹션 제목 + 부제. 사용 방법·제품 사양이 같은 모양이다.
 * 데스크탑 시안 폭 1196 = `max-w-screen-checkout-max`, 모바일은 전폭.
 */
const SectionHeader = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <div className="mx-auto flex w-full max-w-screen-checkout-max flex-col items-center gap-3 text-center text-coolgray-90 laptop:gap-4">
    <h2 className="text-[24px] leading-[26px] laptop:text-5xl">{title}</h2>
    <p className="text-[14px] leading-[22px] tracking-[0.5px] laptop:text-lg">
      {description}
    </p>
  </div>
);

/* ------------------------------------------------------------------ *
 * 제품 소개 — 2카드 (Desktop `30745:38949` · Mobile `30812:234930`)
 * ------------------------------------------------------------------ */

/**
 * 소개 카드(이미지 위 · 텍스트 아래).
 *
 * 제목은 모바일만 **Medium**(시안 `30854:38634` Noto Sans Medium 18/26), 데스크탑은
 * Regular 22/28(`31154:31621`)이다.
 */
const IntroCard = ({
  figure,
  mobileFigure,
  figureAlt,
  figureClassName,
  title,
  description,
  descriptionClassName,
}: {
  figure: FigureSlot;
  /** 좁은 폭에서 **구도가 다른** 컷이 따로 있을 때만(3단 풍속 하나뿐이다). */
  mobileFigure?: FigureSlot;
  figureAlt: string;
  /** 커버 박스(종횡비·배경). 카드마다 시안 규격이 다르다. */
  figureClassName: string;
  title: string;
  description: string;
  /** 번역 값의 `\n` 을 어느 폭에서 살릴지 — 위 docblock 「문장 경계에서 줄바꿈」. */
  descriptionClassName: string;
}) => (
  <div className="flex flex-col gap-4 laptop:gap-5">
    {/*
      ★**고정 높이가 아니라 종횡비**다 — `h-* w-full` 은 폭만 유동이라 360 을 벗어나는 순간
      `object-cover` 가 세로를 잘라낸다(2026-09-16 실측: 768 에서 보이는 세로 45%).
    */}
    <CoolingFanFigure
      slot={figure}
      mobileSlot={mobileFigure}
      alt={figureAlt}
      className={cn("w-full rounded-2xl", figureClassName)}
    />
    <div className="flex flex-col gap-2 text-coolgray-90 laptop:gap-3">
      <h3 className="text-[18px] font-500 leading-[26px] laptop:text-2xl laptop:font-400">
        {title}
      </h3>
      <p
        className={cn(
          "text-[14px] leading-[22px] laptop:text-lg laptop:tracking-[0.5px]",
          descriptionClassName,
        )}
      >
        {description}
      </p>
    </div>
  </div>
);

export const CoolingFanIntroSection = () => {
  const { t } = useTrans(["tactsuit"]);

  return (
    <Container>
      <div className="flex flex-col gap-8 laptop:grid laptop:grid-cols-2 laptop:items-start laptop:gap-6">
        {/*
          모바일 커버(`31190:38290` 328×231.638)는 데스크탑 컷(708×500)과 **같은 그림의 같은
          비율**(1.416)이라 한 장으로 양쪽을 쓴다.
        */}
        <IntroCard
          figure={COOLING_FAN_FIGURES.introHero}
          figureAlt={t("Airflow diagram of Cooling Fan for TactSuit Pro")}
          figureClassName="aspect-[328/232] bg-coolgray-90 laptop:aspect-[708/500]"
          title={t("Cooler TactSuit Pro")}
          description={t(
            "Attach it to the inside back panel and the fan pushes air upward. Heat does not sit on your back, so long sessions stay comfortable. No need to change your device. At 462.5g it goes straight onto the TactSuit Pro you already own.",
          )}
          descriptionClassName="whitespace-pre-line"
        />
        <IntroCard
          figure={COOLING_FAN_FIGURES.introFanSpeed}
          mobileFigure={COOLING_FAN_FIGURES.introFanSpeedMobile}
          figureAlt={t("Cable controller with three fan speed levels")}
          figureClassName="aspect-[328/232] bg-coolgray-90 laptop:aspect-[708/500]"
          title={t("Three fan speeds")}
          description={t(
            "A controller on the cable sets the airflow to one of three levels. You can change it by hand without stopping play.",
          )}
          descriptionClassName="laptop:whitespace-pre-line"
        />
      </div>
    </Container>
  );
};

/* ------------------------------------------------------------------ *
 * 사용 방법 — STEP 3 + 충전 안내 (Desktop `30765:38961` · Mobile `30812:234943`)
 * ------------------------------------------------------------------ */

const StepCard = ({
  figure,
  figureAlt,
  step,
  title,
  description,
  note,
}: {
  figure: FigureSlot;
  figureAlt: string;
  step: string;
  title: string;
  description: string;
  /** STEP 03 에만 있다(시안 `30765:45413` · 모바일 `30812:234977`). */
  note?: string;
}) => (
  <div className="flex flex-col gap-5">
    {/*
      모바일 커버(`31190:38415` 등 328×213)는 데스크탑 컷과 같은 그림의 축소 크롭이다
      (1.540 vs 1.536) — 한 장으로 양쪽을 쓴다.
    */}
    <CoolingFanFigure
      slot={figure}
      alt={figureAlt}
      className="aspect-[328/213] w-full rounded-2xl laptop:aspect-[464/302]"
    />
    <div className="flex flex-col gap-3">
      {/*
        모바일은 배지가 제목 **위**(시안 `30812:234952` flex-col gap 8), 데스크탑은 옆
        (`30765:45410` gap 12).
      */}
      <div className="flex flex-col items-start gap-2 laptop:flex-row laptop:items-center laptop:gap-3">
        {/*
          시안의 Badges 컴포넌트와 값이 그대로 맞는다 —
          bg #EDF5FF=blue-10 · text #0053FF=blue-hover-70 · 12/16 600 · px6 py2 · r4 · h20.
        */}
        <Badge variant="new" size="sm" className="shrink-0">
          {step}
        </Badge>
        <h3 className="text-[18px] leading-[26px] text-coolgray-90 laptop:text-2xl">
          {title}
        </h3>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-[14px] leading-[22px] tracking-[0.25px] text-coolgray-90 laptop:text-base">
          {description}
        </p>
        {note && (
          <p className="text-sm leading-[18px] tracking-[0.4px] text-coolgray-60 laptop:text-sm">
            {note}
          </p>
        )}
      </div>
    </div>
  </div>
);

/**
 * 충전 안내 다크 카드 한 장.
 *
 * - 모바일: **이미지 위 320×192 / 텍스트 패널 아래**(시안 `30841:48131` · 패널 px12 py20 ·
 *   카드 전체 r8).
 * - 데스크탑: **사진이 668×400 카드 전체를 덮고** 텍스트가 left 40 · 세로 중앙에 얹힌다
 *   (시안 `31045:38325`). 보드는 좌우 반전 좌표계라 `x=383 w=245` 를 668 에서 되돌려
 *   left = 668 − (383 + 245) = **40**, top 140 = (400 − 120) / 2 로 환산했다.
 *
 * 두 폭의 사진이 **크롭이 다른 별도 컷**이라 `<picture>` 아트 디렉션으로 고른다.
 *
 * 항목 라벨의 `\n` 은 모바일에서만 산다 — 시안이 「노랑·파랑 고정 / (후면 단자 LED)」를
 * 모바일에서만 두 줄로 나눴다(`30841:48118`). 데스크탑(`31045:38334`)은 한 줄.
 *
 * ## ★데스크탑 텍스트 폭은 **모듈 경계에서 계산**한다
 *
 * 카드 높이는 400 고정이고 폭만 뷰포트를 따른다. 사진(1336×800)은 `object-cover` 로 높이
 * 400 에 맞춰 668 폭이 되고 가운데 정렬되므로, 카드 폭 W 에서 모듈 왼쪽 경계는
 * `(W − 668) / 2 + 317.5 = W/2 − 16.5` 다(317.5 = 원본 x 635 × 0.5 · 밝기 프로파일로 잼).
 * 텍스트 패널을 `max-w: calc(50% − 25px)` · 왼쪽 패딩 40 · 오른쪽 0 으로 두면 텍스트 오른쪽
 * 끝이 `W/2 − 25` 에서 멈춰 **경계보다 8.5px 앞**에서 끝난다 — 폭·로케일과 무관하다.
 *
 * 폭 제한이 없던 때는 KR 1440 만 안 겹쳤다(리뷰 R2 1번 · 2026-09-29 재측정: ko·en·de ×
 * 1024·1280·1352·1440 두 카드 24칸 중 13칸 겹침). KR 1440 은 상한 259 > 실제 235 라
 * 줄바꿈 없이 시안 그대로다. 긴 로케일은 줄이 늘고 블록이 세로 중앙에서 자란다.
 *
 * ⚠️ 폭 제한만으로는 **공백 없는 긴 단어**가 넘친다 — de 1024 에서 「Zubehörverbindung」이
 * 패널 195 를 넘어 228 까지 나갔다(+24 겹침). `hyphens-auto`(`<html lang>` 이 로케일로 잡혀
 * 있어 분철 사전이 붙는다) + `break-words`(사전이 없을 때의 바닥)로 단어 안에서 끊는다.
 * 재측정 스크립트는 PR #5661 R2 리뷰의 접기 블록.
 */
const ChargingCard = ({
  title,
  items,
  figure,
  mobileFigure,
  figureAlt,
}: {
  title: string;
  items: { label: string; ok: boolean }[];
  figure: FigureSlot;
  mobileFigure: FigureSlot;
  figureAlt: string;
}) => (
  <div className="flex flex-col overflow-hidden rounded-lg bg-coolgray-90 text-white laptop:relative laptop:h-[400px] laptop:justify-center laptop:rounded-2xl">
    <CoolingFanFigure
      slot={figure}
      mobileSlot={mobileFigure}
      alt={figureAlt}
      className="aspect-[320/192] w-full shrink-0 laptop:absolute laptop:inset-0 laptop:aspect-auto laptop:h-full"
    />
    <div className="flex flex-col gap-2 px-3 py-5 laptop:relative laptop:max-w-[calc(50%-25px)] laptop:gap-3 laptop:py-0 laptop:pl-10 laptop:pr-0">
      <h4 className="text-lg leading-[26px] tracking-[0.5px] laptop:hyphens-auto laptop:break-words laptop:text-2xl laptop:tracking-normal">
        {title}
      </h4>
      <ul className="flex flex-col gap-1">
        {items.map(({ label, ok }) => (
          <li
            key={label}
            className="flex items-center gap-1 laptop:items-start"
          >
            {/* 라벨이 여러 줄이 되면 아이콘이 flex 에서 눌리지 않게 한다. */}
            {ok ? (
              <CheckMarkIcon className="shrink-0" />
            ) : (
              <CrossMarkIcon className="shrink-0" />
            )}
            <span className="min-w-0 whitespace-pre-line text-base tracking-[0.25px] laptop:hyphens-auto laptop:whitespace-normal laptop:break-words laptop:text-lg laptop:tracking-[0.5px]">
              {label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const CoolingFanHowToSection = () => {
  const { t } = useTrans(["tactsuit"]);

  /**
   * ⚠️ 아이콘은 「좋다/나쁘다」가 아니라 **그 상태에서 일어나는 일**을 가리킨다 — 시안
   * 그대로다. `충전 안 됨` 이 ✗ 인 이유가 그것이다(팬을 쓰는 동안 충전이 멈춘다).
   *
   * ⚠️ 2026-09-16 시안에서 **4항목 → 3항목**이 됐다. 종전의 `Rear port LED` ✗ 단독
   * 항목이 사라지고 `노랑·파랑 고정` 라벨 안으로 들어갔다. 항목만 지우고 라벨을 안
   * 바꾸면 「후면 단자 LED 가 어떻게 되는지」 안내가 통째로 사라진다.
   */
  const fanOnItems = [
    { label: t("Allow accessory connection: on"), ok: true },
    { label: t("Yellow and blue stay lit (rear port LED)"), ok: true },
    { label: t("Not charging"), ok: false },
  ];
  const chargingItems = [
    { label: t("Allow accessory connection: off"), ok: true },
    { label: t("Cooling fan stops"), ok: true },
    { label: t("Charging starts"), ok: true },
  ];

  return (
    <Container>
      <div className="flex flex-col gap-6 laptop:gap-8">
        <SectionHeader
          title={t("How to use")}
          description={t(
            "Attach it, connect the cable, then switch it on in bHaptics Player and you are ready to go.",
          )}
        />

        <div className="flex flex-col gap-6 laptop:gap-[52px]">
          <div className="flex flex-col gap-8 laptop:grid laptop:grid-cols-3 laptop:items-start laptop:gap-6">
            <StepCard
              figure={COOLING_FAN_FIGURES.stepAttach}
              figureAlt={t("Attaching the cooling fan to the back panel")}
              step="STEP 01"
              title={t("Attach it")}
              description={t(
                "Stick it to the inside back panel of the TactSuit Pro with velcro. The fan end hangs below the hem of the suit.",
              )}
            />
            <StepCard
              figure={COOLING_FAN_FIGURES.stepConnect}
              figureAlt={t("Connecting the USB Type-C cable")}
              step="STEP 02"
              title={t("Connect USB Type-C")}
              description={t(
                "Connect the cable to the USB Type-C port on the TactSuit Pro module.",
              )}
            />
            <StepCard
              figure={COOLING_FAN_FIGURES.stepPlayer}
              figureAlt={t("bHaptics Player accessory connection setting")}
              step="STEP 03"
              title={t("Set up bHaptics Player")}
              description={t(
                "When it draws power from the TactSuit Pro, turn on 'Allow accessory connection' in bHaptics Player preferences.",
              )}
              note={t(
                "It runs while the suit battery is above 20%. Please turn it off while charging.",
              )}
            />
          </div>

          {/*
            모바일에서 이 블록만 **풀블리드**다(시안 `30812:234983` 이 w=360 · x=0 으로
            `Section_HowTo` 의 px-16 을 벗어난다). `Container` 의 mx-4 를 음수 마진으로
            되돌린다 — 데스크탑은 시안대로 라운드 카드라 `laptop:mx-0` 로 복귀시킨다.
          */}
          <div className="-mx-4 bg-coolgray-10 px-5 py-6 laptop:mx-0 laptop:rounded-2xl laptop:p-10">
            <div className="flex flex-col gap-5 laptop:gap-6">
              <div className="flex flex-col gap-2 text-coolgray-90">
                <h3 className="text-[20px] leading-[26px] laptop:text-3xl">
                  {t("Turn it off while charging")}
                </h3>
                <p className="text-[14px] leading-[22px] tracking-[0.25px] laptop:text-base">
                  {t(
                    "You can't use the cooling fan and charge the battery at the same time. To charge the battery, turn off 'Allow accessory connection' in bHaptics Player.",
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-5 laptop:grid laptop:grid-cols-2 laptop:gap-6">
                <ChargingCard
                  title={t("While using the cooling fan")}
                  items={fanOnItems}
                  figure={COOLING_FAN_FIGURES.chargingFanOn}
                  mobileFigure={COOLING_FAN_FIGURES.chargingFanOnMobile}
                  figureAlt={t("LED state while the cooling fan is running")}
                />
                <ChargingCard
                  title={t("While charging the battery")}
                  items={chargingItems}
                  figure={COOLING_FAN_FIGURES.chargingBattery}
                  mobileFigure={COOLING_FAN_FIGURES.chargingBatteryMobile}
                  figureAlt={t("LED state while the battery is charging")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

/* ------------------------------------------------------------------ *
 * 제품 사양 (Desktop `30765:45320` · Mobile `30812:235003`)
 * ------------------------------------------------------------------ */

/**
 * 사양 한 칸. 모바일은 **한 패널 안의 2×2 칸**(왼쪽 정렬 · 배경 없음 · 시안 `31169:31623`),
 * 데스크탑은 **흰 타일 4개**(가운데 정렬 · `30995:38410`)다. 같은 DOM 을 폭으로 가른다.
 *
 * `value` 는 번역 키다(`null` 이면 미확정 → `—`).
 *
 * ⚠️ 데스크탑 타일은 시안 높이 117.333 을 **최소 높이**로 둔다. 시안은 값이 전부
 * `0000000`(7자)이라 한 줄이지만, 확정된 「TactSuit Pro 전용」은 22px 에서 타일 안쪽 폭
 * (182 − 64)을 넘어 두 줄이 된다 — 고정 높이면 넘친다(2026-09-29 1440 실측: 라벨이 위로
 * 밀림). 그리드 칸은 기본 `stretch` 라 4칸이 함께 늘어난다.
 *
 * 세로는 **상단 정렬**이다(2026-10-08 사용자 지시). 시안은 값이 전부 한 줄이라 중앙이었지만, 확정값은
 * 칸마다 줄 수가 달라(라벨 「Cooling method」·「Compatible devices」 두 줄, 값 「460 × 350 × 58mm」 두 줄)
 * 중앙이면 라벨 높이가 칸마다 어긋났다.
 */
const SpecCell = ({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) => {
  const { t } = useTrans(["tactsuit"]);

  return (
    <div className="flex flex-col gap-1.5 laptop:min-h-[117.333px] laptop:items-center laptop:justify-start laptop:gap-3 laptop:rounded-2xl laptop:bg-white laptop:p-8 laptop:text-center">
      <p className="text-[13px] leading-[20px] text-coolgray-60 laptop:text-lg laptop:tracking-[0.5px]">
        {label}
      </p>
      <p className="text-[16px] leading-[24px] text-coolgray-90 laptop:text-2xl">
        {value ? t(value) : "—"}
      </p>
    </div>
  );
};

export const CoolingFanSpecsSection = () => {
  const { t } = useTrans(["tactsuit"]);
  const drawingAlt = t("Dimension drawing of Cooling Fan for TactSuit Pro");

  return (
    <Container>
      <div className="flex flex-col gap-6 laptop:gap-15">
        <SectionHeader
          title={t("Specifications")}
          description={t(
            "Check the size and weight, how it cools, and which device it fits.",
          )}
        />

        {/*
          ★내부 순서가 두 보드에서 반대다 — 모바일은 `도면 → 패널`(시안 `30812:235007`),
          데스크탑은 `타일 → 도면`(시안 `30765:45367`). DOM 은 모바일 순서로 두고
          데스크탑에서만 `order` 로 뒤집는다.

          ⚠️ **받아들인 것**: 데스크탑에서 DOM 순서(도면 → 타일)와 시각 순서가 어긋난다.
          둘 다 포커스 가능한 요소가 없어 탭 순서는 영향이 없고, 두 벌 렌더보다 이쪽이 낫다고
          봤다. 도면에 링크·버튼이 생기면 이 판단을 다시 한다.
        */}
        <div className="flex flex-col gap-6 laptop:gap-12.5">
          <div className="overflow-hidden rounded-2xl bg-white laptop:order-2 laptop:flex laptop:justify-center laptop:px-24">
            {/*
              모바일은 Front·Back 두 장을 **한 파일로 합성한** 도면(`31205:38289` 328×308.7),
              데스크탑은 887×700 한 장(`30995:38711`)이다. 두 컷이라 `<picture>` 아트
              디렉션으로 고른다 — 폭 토글이면 숨은 쪽도 받는다(1440 실측 · `cooling-fan-assets.ts`
              `specsDimensionsMobile`).

              박스 비율 = 각 SVG 고유 비율이라 `object-cover` 가 잘라내는 것은 없다.

              ⚠️ 폭은 **`w-full` + `max-w-[887px]`** 다 — 반대(`w-[887px]` + `max-w-full`)로
              두면 퍼센트 `max-width` 는 내재 크기 계산에서 무시돼 최소 폭이 887 + 패딩 192 =
              1079 가 되고, 1024~1118 에서 Container 를 밀어 좌우 여백 20 이 사라졌다
              (리뷰 R3 1번 · 2026-09-29 실측 1024: 0..1024 · 1100: 11..1090).
            */}
            <CoolingFanFigure
              slot={COOLING_FAN_FIGURES.specsDimensions}
              mobileSlot={COOLING_FAN_FIGURES.specsDimensionsMobile}
              alt={drawingAlt}
              className="aspect-[328.326/308.712] w-full laptop:aspect-[887/700] laptop:max-w-[887px]"
            />
          </div>

          {/* 모바일 2×2 한 패널(p20 · 칸 간격 24), 데스크탑 4단 · 시안 폭 800 고정. */}
          <div className="grid grid-cols-2 gap-6 rounded-2xl p-5 laptop:order-1 laptop:mx-auto laptop:w-full laptop:max-w-[800px] laptop:grid-cols-4 laptop:rounded-none laptop:p-0">
            <SpecCell
              label={t("Dimensions")}
              value={COOLING_FAN_SPEC_VALUES.size}
            />
            <SpecCell
              label={t("Cooling method")}
              value={COOLING_FAN_SPEC_VALUES.cooling}
            />
            <SpecCell
              label={t("Weight")}
              value={COOLING_FAN_SPEC_VALUES.weight}
            />
            <SpecCell
              label={t("Compatible devices")}
              value={COOLING_FAN_SPEC_VALUES.compatibility}
            />
          </div>
        </div>
      </div>
    </Container>
  );
};
